import {Box, List, Loader, Select, SelectOptionItem, Typography} from '@logistics-frontend/blocks'
import {useI18n} from '@logistics-frontend/core/i18n'
import {CountryCode} from '@logistics-frontend/core/types'
import React, {useCallback} from 'react'
import {useHistory, useParams} from 'react-router-dom'

import {useCountriesOptions} from '../../hooks'
import {TariffsPageParams, TariffZonesItem, UrlsType} from '../../types'
import {NavListItem} from '../nav-list-item'
import {Search} from '../search'
import {useTariffZoneName} from './CitiesColumn.hooks'

type Props = {
  defaultCountry: string
  urls: UrlsType
  tariffZones: TariffZonesItem[]
  defaultZone?: string
}

const CitiesColumn: React.FC<Props> = ({defaultCountry, defaultZone, urls, tariffZones}) => {
  const t = useI18n()
  const countriesOptions = useCountriesOptions(tariffZones)

  const {country, zone} = useParams<TariffsPageParams>()
  const history = useHistory()

  const isCountryCorrect = !!countriesOptions.find(({value}) => value === country)
  const countryName = isCountryCorrect ? country : defaultCountry
  const {searchResult, updateZoneName, isPending} = useTariffZoneName({
    zone,
    tariffZones,
    defaultZone,
    countryCode: countryName,
    onCommit: validZone => {
      const needRedirect = !zone || !isCountryCorrect

      if (needRedirect) {
        history.replace(urls.zoneUrl({zone: validZone, country: countryName}))
      }
    },
  })

  const handleCountryChange = useCallback(
    (country: SelectOptionItem<CountryCode> | null) => {
      const countryName =
        country && countriesOptions.some(({value}) => value === country?.value) ? country.value : defaultCountry

      updateZoneName({
        countryCode: countryName,
        onCommit: zone => history.push(urls.zoneUrl({country: countryName, zone})),
      })
    },
    [history, urls, countriesOptions, defaultCountry, updateZoneName],
  )

  if (isPending) {
    return <Loader />
  }

  return (
    <>
      <Typography
        Component="h3"
        pb="m"
        pt="m"
        style={{
          paddingInline: 12,
        }}
        variant="body1"
      >
        {t('profile.tariffs.title.countries')}
      </Typography>
      <Box pb="m">
        <Select onChange={handleCountryChange} options={countriesOptions} value={countryName} />
      </Box>
      <Typography
        Component="h3"
        pb="m"
        pt="m"
        style={{
          paddingInline: 12,
        }}
        variant="body1"
      >
        {t('tariffs.column_title.cities')}
      </Typography>
      <Box pb="m">
        <Search />
      </Box>
      <Box pb="m">
        <List
          getId={item => item.name}
          items={searchResult}
          renderItem={(item, {isSelected}) => (
            <NavListItem isSelected={isSelected} title={item.translation} url={urls.zoneUrl({zone: item.name})} />
          )}
          selectedItemId={zone}
        />
      </Box>
    </>
  )
}

export default CitiesColumn
